import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { checkSocialLogin } from "@repo/shopify/api/checkSocialLogin";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import useTracking from "@repo/shopify/hooks/useTracking";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";

import Login from "@organisms/Header/HeaderAccount/Login";
import Register from "@organisms/Header/HeaderAccount/Register";
import Reset from "@organisms/Header/HeaderAccount/Reset";
import { SocialLogin } from "@organisms/Header/HeaderAccount/SocialLogin";
import { SocialLoginCaa } from "@organisms/Header/HeaderAccount/SocialLoginCaa";

import { NOTIFICATIONS } from "@const/notifications";

import useCheckLocation from "@hooks/useCheckLocation";
import useCustomerQuery from "@hooks/useCustomerQuery";
import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const TOSS_EMAILS = ["toss.im", "tosspayments.com", "tossinsight.im"];

export default function HeaderAccount() {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { lang } = useLanguage();

  const { isPC } = useCheckDevice();

  const { openAccount, openModal, openToast, openMenu } = useLayoutStore();
  const { login, update } = useCustomerStore();
  const { createToken, fetchCustomer, createCustomer, resetPassword, createCustomerLoading } = useCustomerQuery();
  const { gtmLogin } = useTracking();
  const { isDanielJohnston } = useCheckLocation();

  const [formType, setFormType] = useState<"login" | "register" | "forgot">("login");

  const isInApp = useMemo(() => {
    const userAgent = navigator.userAgent.toUpperCase();

    return (
      userAgent.includes("NAVER") ||
      userAgent.includes("KAKAO") ||
      userAgent.includes("INSTAGRAM") ||
      userAgent.includes("FBAN") ||
      userAgent.includes("FBAV")
    );
  }, []);

  useEffect(() => {
    if (isInApp) {
      openModal({
        title: t("login.in_app_browser_title"),
        content: (
          <div>
            <Trans i18nKey="login.in_app_browser_message" />
          </div>
        ),
        confirm: {
          label: "Confirm",
          onClick: () => {
            openModal(null);
          },
        },
      });
    }
  }, [isInApp]);

  const submit = async ({ email, password, acceptsMarketing }: { email: string; password: string; acceptsMarketing?: boolean }) => {
    // 소셜로그인 계정인지 비활성화 계정인지 체크
    const response = await checkSocialLogin({ email, domain: lang });

    // 비활성화된 계정일 경우
    if (response?.isDeactivated) {
      openModal({
        title: "Account Access Restricted",
        content: (
          <div>
            <Trans i18nKey="login.account_disable" />
          </div>
        ),
        confirm: {
          label: "Confirm",
          onClick: () => {
            openModal(null);
          },
        },
      });
      return;
    }

    // 소셜 계정 (이메일 로그인 불가)
    if (response && response.loginType !== "email") {
      if (formType === "forgot") openToast({ message: t("success.password_reset_social_email") });
      else openToast(NOTIFICATIONS.WARNING_SOCIAL_ACCOUNT({ social: response.loginType }));
      return;
    }

    switch (formType) {
      case "register": {
        const { data } = await createCustomer({
          variables: { input: { email, password, acceptsMarketing } },
        });
        if (data?.customerCreate.customerUserErrors.length) return;
        await handleLogin({ email, password });
        break;
      }
      case "forgot": {
        await resetPassword({ variables: { email } });
        break;
      }
      case "login":
      default:
        await handleLogin({ email, password });
        break;
    }
  };

  const handleLogin = async ({ email, password }: { email: string; password: string }) => {
    const { data } = await createToken({ variables: { input: { email, password } } });

    if (data?.customerAccessTokenCreate.customerUserErrors.length) {
      return;
    }

    if (!data?.customerAccessTokenCreate.customerAccessToken) return;

    const { data: customerData } = await fetchCustomer({
      variables: { customerAccessToken: data.customerAccessTokenCreate.customerAccessToken.accessToken },
    });

    login(data.customerAccessTokenCreate.customerAccessToken);

    if (customerData?.customer) update(customerData.customer);

    if (TOSS_EMAILS.includes(email.split("@")[1])) {
      if (!customerData?.customer.tags.includes("PARTNERSHIP_TOSS")) {
        openModal({
          title: "Verify Your Account",
          content: "토스 임직원 계정은 이메일 인증을 완료해야 할인 혜택이 적용됩니다. 이메일 수신함을 확인해 주세요.",
          isHideCancelButton: true,
          confirm: {
            label: "Okay",
            onClick: () => {
              openModal(null);
            },
          },
        });
      }
    }

    const loginAfterUrl = sessionStorage.getItem("loginAfterUrl");
    if (loginAfterUrl) {
      navigate(loginAfterUrl);
    }

    sessionStorage.removeItem("loginAfterUrl");

    openAccount(false);
    openToast(NOTIFICATIONS.SUCCESS_LOGIN);
    gtmLogin("email", customerData?.customer?.id?.replace("gid://shopify/Customer/", ""), customerData?.customer?.email ?? undefined);
  };

  return (
    <div
      className={classNames(
        "pc:top-0 pc:h-auto pc:max-h-dvh pc:w-96 pc:pt-4 pc:p-5 fixed top-13.5 right-0 z-60 flex h-dvh w-full flex-col gap-5 bg-white p-4 px-4 pb-4 lg:w-1/4 dark:bg-black dark:text-white",
        isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
      )}
    >
      {isPC ? (
        <div className="flex justify-between">
          <span>{lang !== "jp" ? "Login/Register" : "Account"}</span>
          <Button
            as="text"
            className="pc:visible invisible"
            onClick={() => {
              openAccount(false);
            }}
          >
            Close
          </Button>
        </div>
      ) : (
        <section
          className={classNames(
            "tnt-gap-x fixed top-0 left-0 z-40 mx-auto grid h-13.5 min-h-13.5 w-full grid-cols-8 items-center bg-white p-4 dark:text-white",
            isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
          )}
        >
          <Button
            as="text"
            className="col-span-1 flex"
            onClick={() => {
              openAccount(false);
              openMenu(true);
            }}
          >
            <Icon className="h-3.5 dark:text-white" name="LeftArrow" />
          </Button>
          <Button
            as="text"
            className="col-span-4 text-base!"
            onClick={() => {
              if (formType === "login") {
                openAccount(false);
                openMenu(true);
              } else {
                setFormType("login");
              }
            }}
          >
            {formType === "login" ? "Back" : "Back to Login"}
          </Button>
        </section>
      )}

      <div className="flex flex-col gap-10">
        {lang !== "jp" ? (
          <>
            <div className="flex flex-col gap-5">
              {formType === "login" && (
                <Login
                  onClickFindPassword={() => {
                    setFormType("forgot");
                  }}
                  onClickRegister={() => {
                    setFormType("register");
                  }}
                  submit={submit}
                />
              )}
              {formType === "register" && <Register createCustomerLoading={createCustomerLoading} submit={submit} />}
              {formType === "forgot" && (
                <Reset
                  onClickRegister={() => {
                    setFormType("register");
                  }}
                  submit={submit}
                />
              )}
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="text-title">Social Login</span>
              <SocialLogin />
            </div>
          </>
        ) : (
          <SocialLoginCaa />
        )}
      </div>
    </div>
  );
}
